import { useState } from "react";
import { type DateRange } from "../filters/dateRange";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { FunnelContactCard } from "./FunnelContactCard";
import {
  fetchFunnelBoardStage,
  type FunnelBoardColumn,
  type FunnelBoardContact,
} from "./funnelBoard";

export function FunnelColumn({
  column,
  range,
  leg,
  acquisitionGroup,
  onSelect,
}: {
  column: FunnelBoardColumn;
  range: DateRange;
  leg: string;
  acquisitionGroup: string;
  onSelect: (contact: FunnelBoardContact) => void;
}) {
  // The first page (column.contacts) arrives with the board fetch; subsequent
  // pages are loaded on demand. This component is re-keyed on range/leg by the
  // parent, so this local state resets when the cohort changes.
  const [extra, setExtra] = useState<FunnelBoardContact[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(column.has_more);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadedCount = column.contacts.length + extra.length;

  // Dedupe by id: a contact can shift across offset pages if it advances stage
  // between fetches; render each contact once.
  const seen = new Set<string>();
  const contacts = [...column.contacts, ...extra].filter((contact) => {
    const id = String(contact.id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  const loadMore = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const next = await fetchFunnelBoardStage({
        range,
        leg,
        acquisitionGroup,
        stage: column.key,
        offset: loadedCount,
      });
      setExtra((prev) => [...prev, ...next.contacts]);
      setHasMore(next.has_more);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-72 shrink-0 flex-col rounded-lg border bg-muted/30">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="truncate text-sm font-semibold" title={column.label}>
          {column.label}
        </span>
        <Badge variant="secondary" className="tabular-nums">
          {column.total}
        </Badge>
      </div>
      <div className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto p-2">
        {contacts.length === 0 ? (
          <p className="px-1 py-8 text-center text-xs text-muted-foreground">
            No contacts here in this cohort.
          </p>
        ) : (
          contacts.map((contact) => (
            <FunnelContactCard
              key={contact.id}
              contact={contact}
              onSelect={onSelect}
            />
          ))
        )}
        {error ? (
          <p className="px-1 text-center text-xs text-destructive">{error}</p>
        ) : null}
        {hasMore ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={loadMore}
            disabled={loading}
          >
            {loading ? <Loader2 className="size-4 animate-spin" /> : null}
            Load more
          </Button>
        ) : null}
      </div>
    </div>
  );
}
